import React, { useEffect, useState } from 'react';
import { useMediaBlob } from '../hooks/useMediaBlobUrl';
import SlideVisualStage from './SlideVisualStage';
import type { PresentationSlide } from '../types';

interface Props {
  slide: PresentationSlide | null;
  /** Ad-hoc zwart scherm (sessie.is_blackout) -- los van het slide-type
   * "zwart_scherm", dat een bewust lege slide in de volgorde is. */
  blackout?: boolean;
  /** "full" voor het beamerscherm, "preview" voor de compacte weergave op de
   * telefoon van de spreker. */
  size?: 'full' | 'preview';
  /** Voorbeeld in de Sprekersmodule (miniatuur of groot voorbeeld): media start
   * nooit automatisch en een niet-ladende afbeelding toont een melding. De live
   * beamer gebruikt dit niet en blijft ongewijzigd. */
  previewMode?: boolean;
}

export default function SlideRenderer({ slide, blackout = false, size = 'full', previewMode = false }: Props) {
  const isFull = size === 'full';
  const showsMedia = !!slide && ['afbeelding', 'video', 'audio'].includes(slide.slide_type);
  const { url: mediaUrl, failed: mediaFailed } = useMediaBlob(showsMedia ? slide?.media_id : null);
  const [imageBroken, setImageBroken] = useState(false);
  useEffect(() => { setImageBroken(false); }, [mediaUrl]);
  const autoPlay = isFull && !previewMode;
  const imageUnavailable = previewMode && slide?.slide_type === 'afbeelding'
    && (mediaFailed || imageBroken || !slide?.media_id);

  if (blackout || slide?.slide_type === 'zwart_scherm') {
    return <div className="flex h-full w-full items-center justify-center bg-black" />;
  }

  if (!slide) {
    return (
      <div className={`flex h-full w-full items-center justify-center text-slate-500 ${isFull ? 'text-2xl' : 'text-sm'}`}>
        Geen slide
      </div>
    );
  }

  if (slide.slide_type === 'vrije_slide') {
    // Bewerkbare slide: dezelfde weergave in preview, miniatuur en op de beamer.
    return <SlideVisualStage visual={slide} previewMode={previewMode} />;
  }

  const contentClass = isFull
    ? 'max-w-5xl whitespace-pre-wrap text-center text-4xl font-semibold leading-snug sm:text-5xl'
    : 'whitespace-pre-wrap text-sm text-slate-700';
  const referenceClass = isFull
    ? 'mt-4 text-xl font-medium text-teal-300 sm:text-2xl'
    : 'mt-1 text-xs font-medium text-teal-700';

  return (
    <div className={`flex h-full w-full flex-col items-center justify-center gap-4 p-6 ${isFull ? 'text-white' : 'text-slate-900'}`}>
      {slide.slide_type === 'afbeelding' && mediaUrl && !imageUnavailable && (
        <img
          src={mediaUrl} alt="" className="max-h-full max-w-full object-contain"
          onError={previewMode ? () => setImageBroken(true) : undefined}
        />
      )}
      {imageUnavailable && (
        <p role="status" className={`text-center ${isFull ? 'text-lg text-slate-300' : 'text-xs text-slate-500'}`}>
          Afbeelding niet beschikbaar
        </p>
      )}
      {slide.slide_type === 'video' && mediaUrl && (
        <video src={mediaUrl} controls={!isFull || previewMode} autoPlay={autoPlay} className="max-h-full max-w-full" />
      )}
      {slide.slide_type === 'audio' && mediaUrl && (
        <audio src={mediaUrl} controls autoPlay={autoPlay} />
      )}
      {slide.slide_type === 'bulletpoints' && slide.content && (
        <ul className={`${isFull ? 'max-w-4xl space-y-3 text-3xl sm:text-4xl' : 'space-y-1 text-sm'} list-disc pl-6 text-left`}>
          {slide.content.split('\n').filter(Boolean).map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
      {slide.slide_type === 'citaat' && slide.content && (
        <blockquote className={`${contentClass} italic`}>&ldquo;{slide.content}&rdquo;</blockquote>
      )}
      {!['afbeelding', 'video', 'audio', 'bulletpoints', 'citaat'].includes(slide.slide_type) && slide.content && (
        <p className={contentClass}>{slide.content}</p>
      )}
      {slide.bible_reference && <p className={referenceClass}>{slide.bible_reference}</p>}
    </div>
  );
}
